import React, {useCallback, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Tabs} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import {closeModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import CreateNovaAgentMutation from '../../../../mutations/nova/CreateNovaAgentMutation';
import CreateNovaAgentCategoryMutation from '../../../../mutations/nova/CreateNovaAgentCategoryMutation';
import UpdateNovaAgentMutation from '../../../../mutations/nova/UpdateNovaAgentMutation';
import {AgentFormValues, NovaAgentShape} from '../shared/novaAgentForm';
import {toRelayGlobalId} from '../shared/relayGlobalId';
import {showCreateAgentModal} from '../modals/showCreateAgentModal';
import {showNovaVersionHistoryModal} from '../modals/showNovaVersionHistoryModal';
import {showDeleteNovaCategoryModal} from '../modals/showDeleteNovaCategoryModal';
import {NovaAgentVersionEntry} from '../modals/NovaVersionHistoryModal';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {useWarnOnUnsavedChanges} from '../state/useWarnOnUnsavedChanges';
import {isSaveDisabled} from '../shared/saveState';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaStudioHeader} from './NovaStudioHeader';
import {DeleteCategoryPayload, NovaStudioCatalogue} from './NovaStudioCatalogue';
import {NovaPromptTab} from './NovaPromptTab';
import {NovaAgentMdTab} from './NovaAgentMdTab';
import {StudioAgentEntry, StudioCategoryEntry, StudioTab, useNovaStudioShell} from './useNovaStudioShell';

// Output shape is no longer authored — the create flow always creates single-output agents. The
// value is still sent because the create/update GraphQL inputs require a non-null shape; the update
// resolver ignores it and preserves the stored shape regardless.
const DEFAULT_CREATE_SHAPE: NovaAgentShape = 'SINGLE_OUTPUT';

const Shell = styled.div`
	display: flex;
	flex-direction: row;
	flex-grow: 1;
	min-height: 0;
`;
const MainColumn = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-width: 0;
	min-height: 0;
`;
// The page column is height-bounded (see StudioPageColumn in NovaStudioPage.tsx), so the tab content
// — not the page — has to absorb its own overflow: the Prompt tab alone stacks title, description,
// category chips and two tall editors, which is taller than a laptop viewport. Without this the
// bottom of the User-prompt editor would be unreachable.
const TabContent = styled.div`
	flex-grow: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 24px;
`;
// The tab strip gets breathing room around it so Prompt / agent.md / Deploy aren't crowded against
// the header and the content below.
const TabBar = styled.div`
	padding: 8px 24px 0;
`;
// Shown in the main column while nothing is selected: a single centred prompt, no form.
const EmptyState = styled.div`
	display: flex;
	flex-grow: 1;
	align-items: center;
	justify-content: center;
	color: ${NovaStudioColor.textMuted};
	font-size: 16px;
`;

// Placeholder copy — not localised yet; must go through the translations workflow.
const TAB_LABELS: Record<StudioTab, string> = {
	[StudioTab.Prompt]: 'Prompt',
	[StudioTab.AgentMd]: 'agent.md',
	[StudioTab.Deploy]: 'Deploy',
};
const TAB_ORDER: StudioTab[] = [StudioTab.Prompt, StudioTab.AgentMd, StudioTab.Deploy];

interface Props {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	// The combined working draft (prompt inputs + category) for the selected agent — owned by
	// NovaStudioPage, which also runs the on-demand fetch that seeds it. The shell only threads it
	// through to the Prompt tab and reads its live save-state for the header.
	draft: NovaAgentDraft;
	// The selected agent's version list — fetched alongside the prompt content by the same
	// on-demand detail query (see NovaStudioPage.tsx) and threaded through only to feed the
	// version-history modal opened below. Which entry is current comes from each version's own
	// `isCurrent` flag.
	versions: NovaAgentVersionEntry[];
	// Restores a prior version (mutation + reloading the restored content into the draft) —
	// owned by NovaStudioPage.tsx, which is also what re-runs the detail fetch afterwards.
	onRestoreVersion: (versionNumber: number) => Promise<void>;
	// Whether the selected agent's real content has actually finished loading into the draft —
	// owned by NovaStudioPage.tsx (see its detail-load gate, `isDetailLoadedForSelected`), since
	// only it knows whether the on-demand detail fetch has landed for the current selection.
	// Threaded into both Save triggers (the header button and the ⌘S/Ctrl+S shortcut below) via
	// `isSaveDisabled` so neither can ever persist the draft's empty/stale placeholder values over
	// the agent's real prompt bodies. Defaults to true so callers that don't exercise this gate
	// (e.g. this suite's own tests) keep the old dirty/in-flight-only behavior.
	canSave?: boolean;
	// Fired whenever the catalogue selection changes, so the page can fetch that agent's content
	// on demand and load it into the draft. `selectedAgentId` itself stays owned by
	// `useNovaStudioShell` (it also drives which tab is active), so this is the only way out.
	onSelectedAgentIdChange?: (agentId: string | null) => void;
	// `CreateNovaAgentCategoryMutation`/`UpdateNovaAgentMutation` have no `@connection`-backed way
	// to add a brand-new category or move an agent between categories into the Relay store (see
	// their comments) — the caller (NovaStudioPage) wires this to `ForecastQueryRenderer`'s own
	// `retry` so those cases fall back to a full catalogue refetch instead of a bespoke updater.
	onRefetchCatalogue?: () => void;
}

export function NovaStudioShell({
	categories,
	agents,
	draft,
	versions,
	onRestoreVersion,
	canSave = true,
	onSelectedAgentIdChange,
	onRefetchCatalogue,
}: Props): JSX.Element {
	const shell = useNovaStudioShell({categories, agents});
	const {formatMessage} = useIntl();

	useWarnOnUnsavedChanges(draft.isDirty);

	// Switching away from an agent with unsaved edits reloads the draft with the other agent's
	// content, silently discarding those edits. That is a pure in-app state change, so neither the
	// `beforeunload` prompt nor the router's `history.block` (both wired in useWarnOnUnsavedChanges)
	// can catch it — this confirm is the only guard on that path. Only guards an actual switch away
	// from a currently-selected agent (picking the first agent, or re-picking the current one, can't
	// lose edits). Reuses the same copy as the navigation guard.
	const handleSelectAgent = useCallback(
		(agentId: string): void => {
			const switchingAway = shell.selectedAgentId !== null && agentId !== shell.selectedAgentId;
			if (switchingAway && draft.isDirty && !window.confirm(formatMessage({id: 'common.save_changes_text'}))) {
				return;
			}
			shell.selectAgent(agentId);
		},
		[shell.selectedAgentId, shell.selectAgent, draft.isDirty, formatMessage]
	);

	useEffect(() => {
		onSelectedAgentIdChange?.(shell.selectedAgentId);
	}, [shell.selectedAgentId, onSelectedAgentIdChange]);

	// The guard (no-op while clean or a save is already in flight) lives in `isSaveDisabled` so it
	// covers both this handler's two triggers — the header button and the ⌘S/Ctrl+S shortcut below
	// — identically; nothing here decides on its own whether a save should happen. Depends on the
	// three `draft` fields actually read here, not `draft` itself, so the keydown listener effect
	// below doesn't tear down and re-attach on every render — `draft` is a fresh object every render
	// (see useNovaAgentDraft), which would defeat that effect's own dependency array otherwise.
	const handleSave = useCallback((): void => {
		if (isSaveDisabled(draft.isDirty, draft.saveState, canSave)) {
			return;
		}
		draft.save().catch(() => {
			// The draft's own save-state (Error) already reflects the failure and keeps the edits
			// around for retry — this only prevents an unhandled promise rejection.
		});
	}, [draft.isDirty, draft.saveState, draft.save, canSave]);

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent): void {
			const isSaveShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's';
			if (!isSaveShortcut) {
				return;
			}
			// Always swallow the browser's own Save dialog while the Studio is mounted, whether or
			// not there is anything to save right now.
			event.preventDefault();
			handleSave();
		}
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [handleSave]);

	const categoryOptions = useMemo(() => categories.map(({categoryId, name}) => ({categoryId, name})), [categories]);

	const handleCreateCategory = useCallback(
		(name: string): Promise<{categoryId: string; name: string}> =>
			new Promise((resolve, reject) => {
				Util.CommitMutation(
					CreateNovaAgentCategoryMutation,
					{name},
					response => {
						const category = response.createNovaAgentCategory?.category;
						if (!category) {
							reject(new Error('createNovaAgentCategory returned no category'));
							return;
						}
						// No `@connection` to insert into on this plain list (see the mutation's
						// own comment) — refetch so the new category shows up in the catalogue.
						onRefetchCatalogue?.();
						resolve({categoryId: Util.getUUIdFromBase64String(category.id), name: category.name ?? name});
					},
					true,
					null,
					(transaction, onFailure) => {
						// A true return means the fallback scheduled a retry of this same mutation
						// rather than surfacing an error, so this attempt isn't a final failure — the
						// retry's own success/failure settles this promise (see the create submit
						// below for the same guard).
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('createNovaAgentCategory mutation failed'));
					}
				);
			}),
		[onRefetchCatalogue]
	);

	const handleCreateSubmit = useCallback(
		(values: AgentFormValues): Promise<void> =>
			new Promise((resolve, reject) => {
				if (!values.categoryId) {
					reject(new Error('createNovaAgent requires a categoryId'));
					return;
				}
				const categoryId = values.categoryId;
				Util.CommitMutation(
					CreateNovaAgentMutation,
					{
						name: values.name,
						categoryId,
						title: values.title,
						description: values.description,
						shape: DEFAULT_CREATE_SHAPE,
						categoryConnectionParentId: toRelayGlobalId('NovaAgentCategory', categoryId),
					},
					response => {
						const newAgentGlobalId = response.createNovaAgent?.agent?.node?.id;
						// The create mutation's RANGE_ADD already appended the edge, so the catalogue
						// updates itself; open the new agent (which resets to the Prompt tab).
						//
						// Runs at most once per submit. `closeModal` pops whatever sits on top of the
						// modal stack rather than closing a specific modal, so a second call would
						// dismiss an unrelated modal (e.g. the error modal from the follow-up update
						// below, or one the author opened since). It can genuinely be reached twice:
						// the follow-up update's failure handler opens the agent, and if
						// Util.CommitMutation retried that mutation behind the scenes, the retry's own
						// success/failure handler arrives afterwards and would open it again.
						let hasOpenedCreatedAgent = false;
						const openCreatedAgent = (): void => {
							if (hasOpenedCreatedAgent) {
								return;
							}
							hasOpenedCreatedAgent = true;
							closeModal();
							if (newAgentGlobalId) {
								shell.selectAgent(Util.getUUIdFromBase64String(newAgentGlobalId));
							}
							resolve();
						};
						// The create input carries no prompt bodies (see CreateNovaAgentInput), so if the
						// author typed any at create time, persist them as the agent's first authored
						// version with a follow-up update before opening it.
						const hasPromptContent = values.systemPrompt.trim() !== '' || values.userPromptTemplate.trim() !== '';
						if (!newAgentGlobalId || !hasPromptContent) {
							openCreatedAgent();
							return;
						}
						Util.CommitMutation(
							UpdateNovaAgentMutation,
							{
								id: newAgentGlobalId,
								name: values.name,
								categoryId,
								title: values.title,
								description: values.description,
								shape: DEFAULT_CREATE_SHAPE,
								systemPrompt: values.systemPrompt,
								userPromptTemplate: values.userPromptTemplate,
								// agentMd is deliberately not sent: the update resolver preserves the
								// stored value for any prompt field left out, so omitting it can't
								// blank an agent.md the create path may seed now or later. The author
								// has no agent.md field at create time anyway.
							},
							() => openCreatedAgent(),
							true,
							null,
							(transaction, onFailure) => {
								// The agent was created; only saving its prompts failed. Open the agent
								// first — that closes this modal — and only then let the fallback surface
								// the standard error modal: `closeModal` pops the top of the modal stack,
								// so surfacing first would pop the error the author is meant to read and
								// leave the create modal standing. Opening it either way lets the author
								// re-save the prompts on the Prompt tab instead of creating a duplicate;
								// if the fallback retries the mutation instead of showing an error, the
								// retry's own handlers find the agent already opened (see
								// openCreatedAgent).
								openCreatedAgent();
								onFailure(transaction);
							}
						);
					},
					true,
					null,
					(transaction, onFailure) => {
						// Call the fallback so the standard error modal still fires, and reject
						// so the modal's own inline failure state renders too. The fallback returns
						// true when it has scheduled a retry of this same mutation instead of
						// surfacing an error (a transient network/CORS failure) — that is not a final
						// failure, so leave the submit in flight and let the retry's own success or
						// failure settle this promise, rather than flashing the modal's inline error
						// under a create that is about to succeed.
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('createNovaAgent mutation failed'));
					}
				);
			}),
		[shell]
	);

	// `shell.selectedAgent` (not `selectedAgentId`) is read here so the modal only ever opens for
	// an agent the catalogue still knows about — a since-deleted selection resolves to null and no-ops.
	const openHistoryModal = useCallback((): void => {
		const agent = shell.selectedAgent;
		if (!agent) {
			return;
		}
		showNovaVersionHistoryModal({
			versions,
			onRestore: onRestoreVersion,
		});
	}, [shell.selectedAgent, versions, onRestoreVersion]);

	// The delete mutation itself lives inside DeleteNovaCategoryModal (see showDeleteNovaCategoryModal.tsx) —
	// this only needs to trigger the catalogue refetch once it succeeds, mirroring how
	// handleCreateCategory reacts to CreateNovaAgentCategoryMutation above.
	const openDeleteCategoryModal = useCallback(
		(payload: DeleteCategoryPayload): void => {
			showDeleteNovaCategoryModal({
				...payload,
				onDeleted: () => onRefetchCatalogue?.(),
			});
		},
		[onRefetchCatalogue]
	);

	const openCreateModal = useCallback((): void => {
		showCreateAgentModal({
			categories: categoryOptions,
			existingNames: agents.map(agent => agent.name),
			onSubmit: handleCreateSubmit,
			onCreateCategory: handleCreateCategory,
		});
	}, [categoryOptions, agents, handleCreateSubmit, handleCreateCategory]);

	return (
		<Shell data-cy="nova-studio-shell">
			<NovaStudioCatalogue
				catalogue={shell.catalogue}
				selectedAgentId={shell.selectedAgentId}
				onSelectAgent={handleSelectAgent}
				onCreateAgent={openCreateModal}
				onDeleteCategory={openDeleteCategoryModal}
			/>
			<MainColumn>
				{shell.selectedAgent ? (
					<>
						<NovaStudioHeader
							agentName={shell.selectedAgent.name}
							saveState={draft.saveState}
							isDirty={draft.isDirty}
							canSave={canSave}
							onSave={handleSave}
							onOpenHistory={openHistoryModal}
						/>
						<TabBar>
							<Tabs value={shell.activeTab} onValueChange={tab => shell.setActiveTab(tab as StudioTab)}>
								<Tabs.List>
									{TAB_ORDER.map(tab => (
										<Tabs.Tab key={tab} value={tab} data-cy={`nova-tab-${tab}`}>
											{TAB_LABELS[tab]}
										</Tabs.Tab>
									))}
								</Tabs.List>
							</Tabs>
						</TabBar>
						<TabContent data-cy={`nova-tab-content-${shell.activeTab}`}>
							{shell.activeTab === StudioTab.Prompt ? (
								<NovaPromptTab draft={draft} categoryOptions={categoryOptions} />
							) : shell.activeTab === StudioTab.AgentMd ? (
								<NovaAgentMdTab draft={draft} />
							) : (
								`${TAB_LABELS[shell.activeTab]} tab`
							)}
						</TabContent>
					</>
				) : (
					<EmptyState data-cy="nova-studio-empty">
						{formatMessage({id: 'nova.studio.noAgentSelected', defaultMessage: 'No agent selected'})}
					</EmptyState>
				)}
			</MainColumn>
		</Shell>
	);
}
